// CLIToolsPanel.js — collapsible cheat sheet for the engineer-founder.
// Sits at the bottom of the Metrics tab; outlined and lower-contrast so it
// doesn't compete visually with the data tiles above.

import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Box, IconButton, Collapse, Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TerminalIcon from '@mui/icons-material/Terminal';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const COMMANDS = [
  {
    cmd: 'python3 scripts/llm_usage_report.py --period 7d',
    use: '7-day total at a glance.',
  },
  {
    cmd: 'python3 scripts/llm_usage_report.py --period 30d --by function',
    use: 'Cost broken down by function across the last 30 days.',
  },
  {
    cmd: 'python3 scripts/llm_usage_report.py --uid <uid> --period 30d',
    use: 'Per-user deep dive — paste a uid from the cost-by-user tile.',
  },
  {
    cmd: 'python3 scripts/llm_usage_report.py --period 30d --csv > usage.csv',
    use: 'Spreadsheet export of every per-user / system rollup in the window.',
  },
  {
    cmd: 'python3 scripts/llm_usage_report.py --alerts --period 7d',
    use: 'Recent llm_usage_alerts (per-user, single-call, system-spike).',
  },
];

const CodeRow = ({ cmd, use }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(cmd);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked; do nothing */
    }
  };
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        bgcolor: 'grey.100', borderRadius: 1, px: 1, py: 0.5,
      }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1, fontSize: 12 }}>
          {cmd}
        </Typography>
        <IconButton size="small" onClick={onCopy} title={copied ? 'Copied' : 'Copy'}>
          <ContentCopyIcon fontSize="inherit" sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, ml: 1 }}>
        {use}
      </Typography>
    </Box>
  );
};

const CLIToolsPanel = () => {
  const [open, setOpen] = useState(false);

  return (
    <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
      <CardContent>
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setOpen((v) => !v)}
        >
          <TerminalIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2">CLI tools</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
              Mostly redundant with the tiles above — reach for these only when you need
              per-uid × per-day CSV for spreadsheet reconciliation against the Anthropic
              Console, or when the WebUI itself is down.
            </Typography>
          </Box>
          <IconButton size="small">
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={open} unmountOnExit>
          <Box sx={{ mt: 2 }}>
            <Box sx={{
              mb: 1.5, p: 1,
              border: '1px solid', borderColor: 'warning.light', borderRadius: 1,
              bgcolor: 'warning.lighter',
            }}>
              <Typography variant="caption" color="text.primary" sx={{ display: 'block', lineHeight: 1.4 }}>
                <strong>Heads up:</strong> Google Cloud Application Default Credentials (ADC)
                expire daily. Refresh before running any script below:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, mt: 0.5, ml: 0.5 }}>
                gcloud auth application-default login --no-launch-browser
              </Typography>
            </Box>
            <Stack spacing={0}>
              {COMMANDS.map((c) => <CodeRow key={c.cmd} {...c} />)}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              See <code>platform-docs/developer-guides/architecture/secrets-management.md</code> →
              Application Default Credentials for the full local-CLI setup.
            </Typography>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default CLIToolsPanel;
